import { action, observable } from 'mobx';
import History, { MemoryHistory, createMemoryHistory } from 'history';

import { HistoryService } from 'services/base/history';

export class ModalStore {
  @observable.ref
  visible: boolean;

  @observable.ref
  frozen: boolean;

  constructor(visible = false, frozen = false) {
    this.visible = visible;
    this.frozen = frozen;
  }

  @action.bound
  showModal() {
    if (!this.frozen) {
      this.visible = true;
    }
  }

  @action.bound
  hideModal() {
    if (!this.frozen) {
      this.visible = false;
    }
  }

  @action.bound
  toggleModal() {
    if (this.visible) {
      this.hideModal();
    } else {
      this.showModal();
    }
  }

  @action.bound
  freezeModal() {
    this.frozen = true;
  }

  @action.bound
  unfreezeModal() {
    this.frozen = false;
  }

  @action.bound
  hideOnBackEffect() {
    return HistoryService.block((location, action) => {
      if (action === 'POP') {
        if (this.visible) {
          this.hideModal();
          return false;
        }
      }
    });
  }
}

export type ModalMemoryHistory = MemoryHistory & {
  position: number;
  stack: History.Location[];
};

export class HistoryModalStore extends ModalStore {
  readonly history: ModalMemoryHistory;

  constructor(visible = false, frozen = false) {
    super(visible, frozen);

    const history = createMemoryHistory();

    this.history = {
      ...history,
      position: 0, stack: [],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      push: (path: any, state?: any) => {
        if (this.frozen) return;
        this.history.position++;
        history.push(path, state);
        this.history.stack.push(history.location);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      replace: (path: any, state?: any) => {
        if (this.frozen) return;
        history.replace(path, state);
        this.history.stack[this.history.position] = history.location;
      },
      goForward: () => {
        if (this.frozen) return;
        this.history.position++;
        history.goForward();
        this.history.stack.push(history.location);
      },
      goBack: () => {
        if (this.frozen) return;
        this.history.stack.pop();
        this.history.position--;
        if (this.history.position < 0) {
          this.history.position = 0;
          this.hideModal();
        } else {
          history.goBack();
        }
      },
    };
  }

  handleGoBack() {
    return HistoryService.block((location, action) => {
      if (action === 'POP') {
        if (this.visible) {
          this.history.goBack();
          return false;
        }
      }
    });
  }

  @action.bound
  goBackWhile(shouldGoBack: (location: History.Location, position: number) => boolean) {
    while (shouldGoBack(this.history.location, this.history.position)) {
      this.history.goBack();
    }
  }

  @action.bound
  showModalAt(path: string) {
    if (this.frozen) return;
    this.goBackWhile((location, position) => position > 0);
    if (this.history.position === 0) {
      this.history.replace(path);
    } else {
      this.history.push(path);
    }
    this.showModal();
  }

  @action.bound
  dismissModal() {
    if (this.frozen) return;
    this.goBackWhile((location, position) => position > 0);
    this.hideModal();
  }

}
